import React from "react";
import { Box } from "@mui/system";
import { getInstalledBaseTypeIcon } from "@dreebit-registry/vsm-microservice-components";
import { Trans, useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { InstalledBaseNodeTypeKey } from "../../codegen/graphql";
import EventFollowUpPrompt, { EventFollowUp } from "./EventFollowUpPrompt";

interface Props {
    assetId?: string;
    assetLocationId?: string;
    systemId: string;
    systemTitle: string;
    /** Navigate to the system's IoT edit page and close the modal. */
    onUpdate: () => void;
    /** Close the modal without navigating to the IoT edit page. */
    onClose: () => void;
}

const SystemReference = ({ children }: { children?: React.ReactNode }) => (
    <Box
        component="span"
        sx={{ display: "inline-flex", alignItems: "center", gap: "3px", verticalAlign: "middle", fontWeight: "bold" }}
    >
        {getInstalledBaseTypeIcon(InstalledBaseNodeTypeKey.SYSTEM, 18)}
        {children}
    </Box>
);

/** The IoT-config follow-up, reusable by any post-event prompt. */
export const buildIotFollowUp = (t: TFunction, systemTitle: string, onAction: () => void): EventFollowUp => ({
    key: "iot",
    description: (
        // One sentence with the system inline, so languages can reorder it.
        <Trans
            t={t}
            i18nKey="Because of this event, the IoT configuration of the system <1>{{systemTitle}}</1> may need to be updated. Do you want to update it now?"
            values={{ systemTitle }}
            components={{ 1: <SystemReference /> }}
        />
    ),
    actionLabel: t("Update System"),
    onAction,
    testId: "event-followup-iot",
});

// Shown after a path-changing event (install/exchange/remove/move) when the asset's
// old or new location sits under an IoT-configured system. Thin wrapper around the
// generic follow-up prompt for callers that only ever offer the IoT update.
const EventIotUpdatePrompt = ({ assetId, assetLocationId, systemTitle, onUpdate, onClose }: Props) => {
    const { t } = useTranslation();
    return (
        <EventFollowUpPrompt
            assetId={assetId}
            assetLocationId={assetLocationId}
            followUps={[buildIotFollowUp(t, systemTitle, onUpdate)]}
            onClose={onClose}
            closeLabel={t("Close without updating")}
        />
    );
};

export default EventIotUpdatePrompt;
